import classNames from "classnames";
import DropdownTreeSelect from "react-dropdown-tree-select";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IconChevronDown } from "@pentabd/icons";
import { InputSelect, Select, Tag, Text } from "@pentabd/ui";

import { useBaseData } from "@hooks/useBaseData";
import { useLanguage } from "@hooks/useLanguage";
import { useMasterData } from "@hooks/useMasterData";

import {
  getConvertedData,
  getConvertedPositionDataWithPostType,
  getConvertedUnitData,
} from "@helpers/baseDataConvert";
import { getPositionColor } from "@helpers/getPositionColor";
import { unitTreeDataConvert } from "@helpers/unitTreeDataConvert";

import { BASE_DATA } from "@constants/base-data";

import { PositionType } from "@type/HRM/position";

import { GENERAL_INFO } from "@validations/HRM/employee/generalInfoValidation";

const { WINGS, POSITIONS, UNIT_TREE, POST_TYPE } = BASE_DATA;

export function SelectEmployee({
  registeredName,
  selectSingleEmployee,
  forModal,
  cols,
}: {
  registeredName: string;
  selectSingleEmployee?: boolean;
  portal?: boolean;
  zIndex?: number;
  forModal?: boolean;
  cols?: 2 | 3 | 4 | 6;
}) {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  // Those data are for some dropdowns or radio buttons options.
  const { data: positions } = useBaseData(POSITIONS);
  const { data: wings } = useBaseData(WINGS);
  const { data: unitTree } = useBaseData(UNIT_TREE);
  const { data: postType } = useMasterData(POST_TYPE);

  let wing = watch(
    `${GENERAL_INFO.WING_DESK_IO_E0}-${registeredName}`,
  ) as string;
  let designation = watch(
    `${GENERAL_INFO.DESIGNATION}-${registeredName}`,
  ) as string;

  return (
    <div>
      <div className="row">
        {/* <div className={forModal ? "col-12" : `col-xl-12 col-2xl-5`}> */}
        <div
          className={classNames({
            "col-12 col-xl-4 col-2xl-2": cols === 2,
            "col-12 col-xl-4 col-2xl-3": cols === 3,
            "col-12 col-xl-4": cols === 4,
            "col-12 col-sm-6": cols === 6,
            "col-12 h-100": forModal,
            "col-xl-12 col-2xl-5": !cols && !forModal,
          })}
        >
          <Controller
            control={control}
            name={`${GENERAL_INFO.WING_DESK_IO_E0}-${registeredName}`}
            render={({ field }) => {
              return (
                <DropdownTreeSelect
                  data={unitTreeDataConvert(
                    unitTree[0]?.children || [],
                    language,
                  )}
                  onChange={(e) =>
                    setValue(
                      `${GENERAL_INFO.WING_DESK_IO_E0}-${registeredName}`,
                      e.id as number,
                    )
                  }
                  mode="radioSelect"
                  texts={{
                    placeholder: t(
                      "EMPLOYEE.GENERAL_INFO.FORM.WING.PLACEHOLDER",
                    ),
                  }}
                  className="w-100"
                />
              );
            }}
          />
        </div>

        <div
          className={classNames({
            "col-12 col-xl-4 col-2xl-2": cols === 2,
            "col-12 col-xl-4 col-2xl-3": cols === 3,
            "col-12 col-xl-4": cols === 4,
            "col-12 col-sm-6": cols === 6,
            "col-12": forModal,
            "col-xl-12 col-2xl-5": !cols && !forModal,
          })}
        >
          <Controller
            control={control}
            name={`${GENERAL_INFO.WING_DESK_IO_E0}-${registeredName}`}
            render={({ field }) => {
              return (
                <InputSelect
                  {...register(
                    `${GENERAL_INFO.WING_DESK_IO_E0}-${registeredName}`,
                    {
                      required: "Please Select Employee",
                    },
                  )}
                  options={getConvertedUnitData(wings, language)}
                  defaultValue={field.value as number}
                  placeholder={
                    language === "en" ? "Selected Wing" : "নির্বাচিত উইং"
                  }
                  disabled
                  error={errors as any}
                />
              );
            }}
          />
        </div>

        {wing && (
          // <div className={forModal ? "col-12" : `col-xl-12 col-2xl-3`}>
          <div
            className={classNames({
              "col-12 col-xl-4 col-2xl-2": cols === 2,
              "col-12 col-xl-4 col-2xl-3": cols === 3,
              "col-12 col-xl-4": cols === 4,
              "col-12 col-sm-6": cols === 6,
              "col-12": forModal,
              "col-xl-12 col-2xl-3": !cols && !forModal,
            })}
          >
            <Controller
              control={control}
              name={`${GENERAL_INFO.DESIGNATION}-${registeredName}`}
              render={({ field }) => {
                return (
                  <Select
                    {...register(
                      `${GENERAL_INFO.DESIGNATION}-${registeredName}`,
                    )}
                    // options={getConvertedData(
                    //   _.uniqBy(
                    //     positions
                    //       .filter(
                    //         (position: PositionType) =>
                    //           position.unit.id === Number(wing) &&
                    //           position.employee.id !== null,
                    //       )
                    //       .map((position: PositionType) => {
                    //         return {
                    //           id: position.position.id,
                    //           name: position.position.name,
                    //         };
                    //       }),
                    //     "id",
                    //   ),
                    // )}
                    options={getConvertedPositionDataWithPostType(
                      positions
                        .filter(
                          (position: PositionType) =>
                            position.unit.id === Number(wing) &&
                            position.employee.id !== null,
                        )
                        .map((position: PositionType) => ({
                          id: position.position.id,
                          name: position.position.name,
                          position: position.position,
                          postType: position.postType,
                        })),
                      language,
                    ).map((position: any) => ({
                      ...position,
                      customComponent: (
                        <Text
                          color={getPositionColor(
                            postType as any,
                            position as any,
                          )}
                        >
                          {position.label}
                        </Text>
                      ),
                    }))}
                    placeholder={t(
                      "EMPLOYEE.GENERAL_INFO.FORM.DESIGNATION.PLACEHOLDER",
                    )}
                    isSearchable
                    defaultValue={field.value as number}
                    onSelectItem={(designation) => {
                      setValue(
                        `${GENERAL_INFO.DESIGNATION}-${registeredName}`,
                        designation as number,
                      );
                    }}
                    suffix={<IconChevronDown size="16" />}
                  />
                );
              }}
            />
          </div>
        )}

        {wing && designation && !selectSingleEmployee && (
          <div
            className={classNames({
              "col-12 col-xl-4 col-2xl-2": cols === 2,
              "col-12 col-xl-4 col-2xl-3": cols === 3,
              "col-12 col-xl-4": cols === 4,
              "col-12 col-sm-6": cols === 6,
              "col-12": forModal,
              "col-xl-12 col-2xl-5": !cols && !forModal,
            })}
          >
            <Controller
              control={control}
              name={`${registeredName}`}
              render={({ field }) => {
                return (
                  <Select
                    {...register(`${registeredName}`)}
                    options={getConvertedData(
                      positions
                        .filter(
                          (position: PositionType) =>
                            position.unit.id === Number(wing) &&
                            position.position.id === Number(designation) &&
                            position.employee.id !== null,
                        )
                        .map((position: PositionType) => {
                          return {
                            ...position.employee,
                            name: position.employee.nameEn,
                          };
                        }),
                    )}
                    defaultValues={field.value}
                    onSelectItem={(data: any) => {
                      setValue(`${registeredName}`, data);
                    }}
                    suffix={<IconChevronDown size="16" />}
                    isMulti
                    renderSelected={(
                      selectedValues: any,
                      removeSelectedValues: any,
                    ) => {
                      return (
                        <>
                          {selectedValues.map((selectedValue: any) => (
                            <Tag
                              key={selectedValue.value}
                              testId={selectedValue.value as string}
                              label={selectedValue.label}
                              testIdLabel={selectedValue.value as string}
                              size="sm"
                              endIconType="close"
                              onClickClose={() =>
                                removeSelectedValues
                                  ? removeSelectedValues(selectedValue)
                                  : () => {
                                      console.log("No action");
                                    }
                              }
                            />
                          ))}
                        </>
                      );
                    }}
                    placeholder={
                      language === "en"
                        ? "Select Employees"
                        : "কর্মচারী নির্বাচন করুন"
                    }
                  />
                );
              }}
            />
          </div>
        )}

        {/* single employee dropdown */}
        {wing && designation && selectSingleEmployee && (
          <div
            className={classNames({
              "col-12 col-xl-4 col-2xl-2": cols === 2,
              "col-12 col-xl-4 col-2xl-3": cols === 3,
              "col-12 col-xl-4": cols === 4,
              "col-12 col-sm-6": cols === 6,
              "col-12": forModal,
              "col-xl-12 col-2xl-5": !cols && !forModal,
            })}
          >
            <Controller
              control={control}
              name={`${registeredName}`}
              render={({ field }) => {
                return (
                  <InputSelect
                    {...register(`${registeredName}`, {
                      required: "Employee ID is required",
                    })}
                    options={getConvertedData(
                      positions
                        .filter(
                          (position: PositionType) =>
                            position.unit.id === Number(wing) &&
                            position.position.id === Number(designation) &&
                            position.employee.id !== null,
                        )
                        .map((position: PositionType) => {
                          return {
                            ...position.employee,
                            name:
                              language === "en"
                                ? position.employee.nameEn
                                : position.employee.nameBn,
                          };
                        }),
                    )}
                    defaultValue={field.value}
                    onSelectItem={(data: any) => {
                      setValue(`${registeredName}`, data);
                    }}
                    suffix={<IconChevronDown size="16" />}
                    error={errors as any}
                    placeholder={
                      language === "en"
                        ? "Select Employee"
                        : "কর্মচারী নির্বাচন করুন"
                    }
                  />
                );
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
